import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  AlertCircle,
  CheckCircle2,
  ClipboardList,
  Clock,
  History,
  Loader2,
  Target,
  TrendingUp,
} from 'lucide-react'
import { format, parseISO } from 'date-fns'
import toast from 'react-hot-toast'
import { cn, formatHours } from '@/lib/utils'
import {
  useAcknowledgeWPPreviousWeekMutation,
  useGetWPPreviousWeekReviewQuery,
  type WPPreviousWeekProjectGroup,
  type WPPreviousWeekTask,
  type WPUnplannedActual,
} from '@/redux/service/weeklyPlanning'

interface PreviousWeekReviewDialogProps {
  isOpen: boolean
  onClose: () => void
  memberUserId: number
  memberName: string
  weekStart: string
  onAcknowledged: () => void
}

const PreviousWeekReviewDialog = ({
  isOpen,
  onClose,
  memberUserId,
  memberName,
  weekStart,
  onAcknowledged,
}: PreviousWeekReviewDialogProps) => {
  const { data, isLoading, isFetching } = useGetWPPreviousWeekReviewQuery(
    { member_user_id: memberUserId, week_start: weekStart },
    { skip: !isOpen }
  )
  const [acknowledge, { isLoading: isAcking }] =
    useAcknowledgeWPPreviousWeekMutation()

  const review = data?.data
  const totals = review?.totals
  const ackRequired = review?.acknowledgement_required ?? false

  const handleAcknowledge = async () => {
    try {
      await acknowledge({
        member_user_id: memberUserId,
        week_start: weekStart,
      }).unwrap()
      toast.success('Previous week acknowledged')
      onAcknowledged()
    } catch (err) {
      const error = err as { data?: { message?: string }; status?: number }
      if (error?.status === 404) {
        // Nothing to ack — let parent move on.
        onAcknowledged()
        return
      }
      toast.error(error?.data?.message ?? 'Failed to acknowledge')
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={o => !o && onClose()}>
      <DialogContent className='!max-w-[1100px] w-[95vw] h-[92vh] p-0 bg-background overflow-hidden flex flex-col gap-0 border border-border rounded-xl shadow-2xl'>
        <DialogHeader className='px-6 py-4 border-b border-border bg-card/60 backdrop-blur-sm shrink-0'>
          <div className='flex items-center gap-3 pr-8'>
            <div className='p-2 bg-primary/10 text-primary rounded-lg shrink-0'>
              <History className='w-5 h-5' />
            </div>
            <div className='flex-1 min-w-0'>
              <DialogTitle className='text-lg font-bold tracking-tight'>
                Review last week
              </DialogTitle>
              <p className='text-xs text-muted-foreground mt-0.5 truncate'>
                <span className='font-medium text-foreground/80'>
                  {memberName}
                </span>
                {review?.previous_week && (
                  <>
                    {' '}
                    <span className='text-muted-foreground/30'>·</span>{' '}
                    {format(parseISO(review.previous_week.week_start), 'd MMM')}{' '}
                    –{' '}
                    {format(
                      parseISO(review.previous_week.week_end),
                      'd MMM yyyy'
                    )}
                  </>
                )}
              </p>
            </div>
            {ackRequired && (
              <span className='shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full text-amber-600 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20'>
                <AlertCircle className='w-3 h-3' />
                Acknowledgement required
              </span>
            )}
          </div>
        </DialogHeader>

        <div className='flex-1 overflow-y-auto bg-background/95'>
          {isLoading || isFetching || !review ? (
            <div className='flex items-center justify-center py-32'>
              <Loader2 className='h-8 w-8 animate-spin text-primary' />
            </div>
          ) : (
            <div className='px-6 py-5 space-y-5'>
              {/* No-plan banner */}
              {!review.plan && (
                <div className='rounded-xl border border-blue-500/20 bg-blue-500/10 p-4 text-sm text-blue-600 dark:text-blue-400 flex items-start gap-2.5 shadow-sm'>
                  <AlertCircle className='w-4 h-4 mt-0.5 shrink-0' />
                  <div>
                    <p className='font-semibold'>
                      No plan was created for last week
                    </p>
                    <p className='text-xs text-blue-600/80 dark:text-blue-400/80 mt-1 leading-relaxed'>
                      Time logged below is shown for reference. You can proceed
                      to plan this week directly.
                    </p>
                  </div>
                </div>
              )}

              {/* Totals grid */}
              {totals && (
                <div className='grid grid-cols-2 md:grid-cols-5 gap-3.5'>
                  <TotalCard
                    icon={<Target className='w-4 h-4' />}
                    label='Planned Billable'
                    value={totals.planned_billable_hrs}
                    tint='bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/10'
                  />
                  <TotalCard
                    icon={<Target className='w-4 h-4' />}
                    label='Planned Non-Bill.'
                    value={totals.planned_non_billable_hrs}
                    tint='bg-muted text-muted-foreground'
                  />
                  <TotalCard
                    icon={<ClipboardList className='w-4 h-4' />}
                    label='Planned Backlog'
                    value={totals.planned_backlog_hrs}
                    tint='bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/10'
                  />
                  <TotalCard
                    icon={<TrendingUp className='w-4 h-4' />}
                    label='Actual Billable'
                    value={totals.actual_billable_hrs}
                    tint='bg-primary/10 text-primary border-primary/10'
                  />
                  <TotalCard
                    icon={<TrendingUp className='w-4 h-4' />}
                    label='Actual Non-Bill.'
                    value={totals.actual_non_billable_hrs}
                    tint='bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/10'
                  />
                </div>
              )}

              <ProjectSection
                title='Planned this week'
                emptyText='No tasks were planned for the main week.'
                groups={review.planned_projects}
              />

              {review.backlog_projects.length > 0 && (
                <ProjectSection
                  title='Planned backlog'
                  emptyText='No backlog tasks were planned.'
                  groups={review.backlog_projects}
                />
              )}

              {review.unplanned_actuals.length > 0 && (
                <UnplannedSection actuals={review.unplanned_actuals} />
              )}
            </div>
          )}
        </div>

        <div className='shrink-0 flex gap-3 px-6 py-4 border-t border-border bg-card'>
          <Button
            variant='outline'
            onClick={onClose}
            disabled={isAcking}
            className='h-10 px-5 font-semibold rounded-xl border-border hover:bg-accent/40 transition-colors'
          >
            Cancel
          </Button>
          <div className='ml-auto'>
            {ackRequired ? (
              <Button
                onClick={handleAcknowledge}
                disabled={isAcking || isLoading || isFetching}
                className='h-10 px-6 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-xl gap-2 shadow-sm transition-all'
              >
                {isAcking ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <CheckCircle2 className='h-4 w-4' />
                )}
                Acknowledge & continue
              </Button>
            ) : (
              <Button
                onClick={onAcknowledged}
                disabled={isLoading || isFetching}
                className='h-10 px-6 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-xl gap-2 shadow-sm transition-all'
              >
                Continue to plan
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface TotalCardProps {
  icon: React.ReactNode
  label: string
  value: number
  tint: string
}

const TotalCard = ({ icon, label, value, tint }: TotalCardProps) => (
  <div className='rounded-xl border border-border bg-card p-3.5 shadow-sm flex items-center gap-3 transition-colors hover:bg-muted/30'>
    <div
      className={cn(
        'h-9 w-9 rounded-lg flex items-center justify-center shrink-0 border border-transparent',
        tint
      )}
    >
      {icon}
    </div>
    <div className='min-w-0'>
      <p className='text-[10px] font-semibold uppercase tracking-wider text-muted-foreground truncate'>
        {label}
      </p>
      <p className='text-xl font-extrabold text-foreground leading-tight tabular-nums'>
        {Number(value || 0).toFixed(1)}
        {/* <span className='text-xs text-muted-foreground/60 font-semibold ml-0.5'>h</span> */}
      </p>
    </div>
  </div>
)

interface ProjectSectionProps {
  title: string
  emptyText: string
  groups: WPPreviousWeekProjectGroup[]
}

const ProjectSection = ({ title, emptyText, groups }: ProjectSectionProps) => (
  <div className='rounded-xl border border-border bg-card shadow-sm overflow-hidden'>
    <div className='px-5 py-3 border-b border-border bg-muted/20 flex items-center justify-between'>
      <p className='text-sm font-bold text-foreground flex items-center gap-2'>
        <ClipboardList className='w-4 h-4 text-muted-foreground/75' />
        {title}
      </p>
      <span className='text-[11px] font-semibold text-muted-foreground'>
        {groups.length} project{groups.length === 1 ? '' : 's'}
      </span>
    </div>
    {groups.length === 0 ? (
      <p className='text-xs text-muted-foreground py-8 text-center bg-card'>
        {emptyText}
      </p>
    ) : (
      <div className='divide-y divide-border/60'>
        {groups.map((g, gi) => (
          <div
            key={`${g.project_id ?? 'none'}-${gi}`}
            className='p-5 space-y-3'
          >
            <div className='flex items-center justify-between'>
              <p className='text-sm font-bold text-foreground/90 truncate'>
                {g.project_name ?? 'Unknown project'}
              </p>
              <span className='text-xs font-semibold text-muted-foreground/80 shrink-0 ml-3 tabular-nums'>
                {formatHours(g.total_hours)}h planned
              </span>
            </div>
            <div className='space-y-2'>
              {g.tasks.map(t => (
                <TaskRow key={t.id} task={t} />
              ))}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
)

const TaskRow = ({ task }: { task: WPPreviousWeekTask }) => {
  const planned = Number(task.estimated_hours || 0)
  const actual = Number(task.actual_hours || 0)
  const delta = actual - planned
  return (
    <div className='flex items-center gap-3 px-3.5 py-3 bg-muted/30 hover:bg-muted/50 rounded-xl border border-border/40 transition-colors'>
      <div className='flex-1 min-w-0'>
        <p className='text-sm font-semibold text-foreground/90 truncate'>
          {task.task_name}
        </p>
        <div className='flex items-center gap-1.5 mt-0.5'>
          <span
            className={cn(
              'text-[10px] font-semibold px-1.5 py-0.5 rounded border',
              task.is_billable
                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
                : 'bg-muted text-muted-foreground border-border'
            )}
          >
            {task.is_billable ? 'Billable' : 'Non-Billable'}
          </span>
        </div>
      </div>
      <div className='flex items-center gap-4 shrink-0 text-xs'>
        <div className='text-right'>
          <p className='text-[9px] uppercase tracking-wider text-muted-foreground/70 font-bold'>
            Planned
          </p>
          <p className='font-semibold text-foreground/80 inline-flex items-center gap-1 tabular-nums'>
            <Clock className='w-3 h-3 text-muted-foreground/60' />
            {planned.toFixed(1)}h
          </p>
        </div>
        <div className='text-right'>
          <p className='text-[9px] uppercase tracking-wider text-muted-foreground/70 font-bold'>
            Actual
          </p>
          <p className='font-bold text-foreground inline-flex items-center gap-1 tabular-nums'>
            <Clock className='w-3 h-3 text-primary/70' />
            {actual.toFixed(1)}h
          </p>
        </div>
        <div className='text-right min-w-[52px]'>
          <p
            className={cn(
              'font-bold tabular-nums',
              delta > 0
                ? 'text-amber-600 dark:text-amber-400'
                : delta < 0
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-muted-foreground/60'
            )}
          >
            {delta > 0 ? '+' : ''}
            {delta.toFixed(1)}h
          </p>
        </div>
      </div>
    </div>
  )
}

const UnplannedSection = ({ actuals }: { actuals: WPUnplannedActual[] }) => (
  <div className='rounded-xl border border-border bg-card shadow-sm overflow-hidden'>
    <div className='px-5 py-3 border-b border-border bg-muted/20 flex items-center justify-between'>
      <p className='text-sm font-bold text-foreground flex items-center gap-2'>
        <TrendingUp className='w-4 h-4 text-purple-500' />
        Off-plan work
      </p>
      <span className='text-[11px] font-semibold text-muted-foreground'>
        {actuals.length} task{actuals.length === 1 ? '' : 's'}
      </span>
    </div>
    <div className='p-5 space-y-2'>
      {actuals.map((a, i) => (
        <div
          key={`${a.clickup_task_id ?? 'none'}-${i}`}
          className='flex items-center gap-3 px-3.5 py-3 bg-muted/30 hover:bg-muted/50 rounded-xl border border-border/40 transition-colors'
        >
          <div className='flex-1 min-w-0'>
            <p className='text-sm font-semibold text-foreground/90 truncate'>
              {a.task_name}
            </p>
            <span
              className={cn(
                'text-[10px] font-semibold px-1.5 py-0.5 rounded border mt-0.5 inline-block',
                a.is_billable
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
                  : 'bg-muted text-muted-foreground border-border'
              )}
            >
              {a.is_billable ? 'Billable' : 'Non-Billable'}
            </span>
          </div>
          <p className='text-sm font-bold text-foreground inline-flex items-center gap-1 shrink-0 tabular-nums'>
            <Clock className='w-3 h-3 text-primary/70' />
            {formatHours(a.actual_hours)}h
          </p>
        </div>
      ))}
    </div>
  </div>
)

export default PreviousWeekReviewDialog
